import * as React from 'react'
import { SystemBase } from '../../library/common/SystemBase';
import httpRequest from './../../service/httpService';
import { IDynamicList } from '../../library/types/IDynamicList'
import { ComboBox, ComboBoxFilterChangeEvent, ComboBoxProps, MultiColumnComboBox, MultiColumnComboBoxProps, MultiSelect, MultiSelectChangeEvent, MultiSelectFilterChangeEvent } from '@progress/kendo-react-dropdowns';
import { filterBy, FilterDescriptor } from '@progress/kendo-data-query';
import { decrypteStoreUserDetail } from '../../service/userService';


interface ISystemDropDownState {
    listItems?: any[],
    filteredItems?: any[],
    selectedItems?: any[],
    isLoading?: boolean,
    nextDataSource?: string
}

const showConsoleInfo = true;

export abstract class SystemBaseList<P = {}, S = {}> extends SystemBase<IDynamicList & P, ISystemDropDownState & S> {

    constructor(props: any) {
        super(props)
        this.state = {
            ...this.state,
            listItems: [],
            filteredItems: [],
            selectedItems: [],
            isLoading: false
        }
    }

    static defaultProps = {
        ...SystemBase.defaultProps,
        autoDisable: true,
        isMultiColumn: false,
        fetchOnFilter: false,
        textField: "Name",
        dataItemKey: "Value",
        placeholder: "- Please Select -",
        className: "input_feild",
        minFilterLength: 3,
        columns: [],
        smartSearch: false,
        filterable: true,
        dropDownType: 'DropDown'
    } as IDynamicList;

    idField: any = this.props.idField || this.props.dataItemKey || 'id';;

    textField: any = this.props.displayField || this.props.textField || 'text';

    static cachePrefix = 'lib-lookup-';

    static cacheDataSet = (lookupname?: string, data?: any[]) => sessionStorage.setItem(`${SystemBaseList.cachePrefix}${lookupname}`, JSON.stringify(data))

    static cacheDataGet = (lookupname?: string) => JSON.parse(sessionStorage.getItem(`${SystemBaseList.cachePrefix}${lookupname}`) || '[]');

    separator = this.props.multiSelectSeparator || ',';

    getValue = (item: any) => item && item[this.idField] || '';

    getText = (item: any) => item && item[this.textField] || '';

    getItem = (listItems: any, value: any): any => {
        var item: any | undefined = undefined;

        if (listItems && listItems.length > 0 && value != undefined && value != null) {
            item = listItems.find((item: any) => item[this.idField] == value)
            if (item === undefined && this.props.dropDownType == "Lookup" && !this.props.multiSelect && value != undefined && value != null && value != "") {
                item = { DataText: value, DataValue: value };
                listItems.push(item);
            }
        }
        return item;
    }

    setValue = (value: any, callback?: () => void) => {
        let item = this.getItem(this.state.filteredItems, value);
        this.setState({ valueObject: item, value: value }, callback);
        this.props.onTextChanged && this.props.onTextChanged(this.getText(item));
    }

    componentDidMount = () => {
        this.setupValidators();
        this.fetchData(this.props.dataSource)
    }

    async componentDidUpdate(prevProps: any) {
        if (prevProps.dataSource != this.props.dataSource) {
            if (this.state.isLoading) {
                this.setState({ nextDataSource: this.props.dataSource })
            }
            else await this.fetchData(this.props.dataSource)
        }
        if (prevProps.searchText != this.props.searchText) {
            this.fetchData(this.props.dataSource, this.props.searchText)
        }

        this.handleDidUpdate(prevProps);
    }


    filterData = (filter: FilterDescriptor) => {
        return filterBy(this.state.listItems, filter);
    };

    onFilterChange = (event: ComboBoxFilterChangeEvent | MultiSelectFilterChangeEvent) => {
        let filter = { ...event.filter };

        if (this.props.smartSearch) {
            if (this.props.minFilterLength && filter.value.length > this.props.minFilterLength) {
                this.fetchData(this.props.dataSource, filter.value)
            }

            else {
                //this.fetchData(this.props.dataSource, '')
                this.setState({ listItems: [], filteredItems: [] });
            }

        } else {
            if (!this.props.minFilterLength || filter.value.length >= this.props.minFilterLength)
                this.setState({ filteredItems: this.filterData(filter) });
            else
                this.setState({ filteredItems: this.state.listItems });
        }
    }

    fetchData = async (datasource: any, filter?: string) => {
        var listItems: any[] = [];
        var response: any;

        if (datasource) {
            this.setState({ isLoading: true })
            // if items are to be cached and present in session
            var cachedItems = SystemBaseList.cacheDataGet(datasource);
            if (this.props.isCached && cachedItems.length > 0) {
                listItems = cachedItems;
            } else {
                switch (this.props.dataSourceType) {
                    case "ARRAY":
                        listItems = [...datasource];
                        break;

                    case "CSV":
                        let items = datasource.split(';')
                        for (var i = 0; i < items.length; i += 2)
                            listItems.push({ [this.idField]: items[i] || "", [this.textField]: items[i + 1] || "" });
                        break;

                    case "URL":
                    default:

                        // fetch data from server

                        try {
                            response = await httpRequest.get(datasource + (filter ? ('/' + filter) : ''));

                            // set data or empty array, try to load kendo datasource first, then array, then empty array, 
                            // also filters null and undefined text values
                            //listItems = (response.data.Data || response.data || []).filter((item) => {
                            //    return item[this.textField] != null && item[this.textField] != undefined
                            //});

                            listItems = (response.data.Data || response.data || []).filter((item, index, array) => {

                                let isNonEmpty = item[this.textField] != undefined && item[this.textField] != null

                                let isActive = (item["IsActive"] == undefined || item["IsActive"] == true)
                                    && (item["Active"] == undefined || item["Active"] == true);

                                let isDistinct = (index === array.findIndex((f) => JSON.stringify(f) === JSON.stringify(item)));

                                return isNonEmpty && isActive && isDistinct
                            });

                        } catch (e) {
                            listItems = [];
                        }
                        break;
                }
            }
            // cache the data
            if (this.props.isCached) SystemBaseList.cacheDataSet(datasource, listItems);

            this.setState({ isLoading: false })
        }

        // if there is any other data source in pipeline to be fetched 
        if (this.state.nextDataSource) {
            // to stop infinite loop we have to set nextdataSource to empty
            var nextDataSource = this.state.nextDataSource;
            this.setState({ nextDataSource: '' }, () => this.fetchData(nextDataSource));
        } else this.setState({ listItems: listItems || [], filteredItems: listItems || [] }, () => this.handleValueChange());
    }

    handleValueChange = (validate?: boolean) => {
        let currItem = this.getItem(this.state.listItems, this.props.value)
        let text = this.props.multiSelect ? '' : this.getText(currItem)
        let selectedItems = this.props.multiSelect ? this.getItemsFromCSV(this.state.listItems, typeof this.props.value === 'string' && this.props.value || '') : [];
        //this.props.onChange && this.props.onChange(event, this.props.name, this.props.value, text);
        this.props.onAfterListUpdated && this.props.onAfterListUpdated(this.state.listItems, this.props.value, text)
        this.props.onTextChanged && this.props.onTextChanged(text);

        this.setState({ value: this.props.value, selectedItems }, () => validate && this.validate())
    }


    // for multi select
    getItemsFromCSV = (items: any[], value: string) => {
        let selectedItems: any[] = [];

        // create list of selected values
        if (typeof value === 'string' && value && value.split(this.separator).length > 0 && items && items.length > 0) {
            value.split(',').map((id: any) => {
                let itemFound = items.find((t: any) => t[this.idField] == id);
                if (itemFound) selectedItems.push({ [this.idField]: itemFound[this.idField], [this.textField]: itemFound[this.textField] });
            })

        }
        return selectedItems;
    }

    // for multiselect
    getCSVFromItems = (items: any[]): string => {
        let result: string = "";
        items.map((item: any, index: any) => result += item[this.idField] + (index != items.length - 1 ? this.separator : ''));
        return result;
    }

    onMultiSelectChange = (event: MultiSelectChangeEvent) => {
        this.handleChange(event, this.getCSVFromItems(event.target.value), '');
    }

    handleDropDownChange = (event: any) => {

        this.handleChange(event, this.getValue(event.target.value), this.getText(event.target.value))
    }

    //    log = (fname: string, params?: any[]) => {
    //        showConsoleInfo && console.log(
    //            `System Base - ${fname} => Name : ${this.props.name} | Prop Value : ${this.props.value} | State Value : ${this.state.value} | IsValid : ${this.state.isValid}`, params && params)
    //    }
}

const ShowLookUpName = (lookupName?: string) => {
    //const user = useSelector((store: ApplicationState) => store.user)
    //const userInfo = user && user.userInfo && decrypteStoreUserDetail(user.userInfo);
    //return userInfo && userInfo.UserSettings && userInfo.UserSettings.showSecurityObjectName == 'true' ? lookupName : "";
}

export default class SystemDropDown extends SystemBaseList {
    constructor(props: any) {
        super(props)
    }

    render() {

        let multiSelect = this.props.multiSelect
        let multiColumn = !multiSelect && (this.props.isMultiColumn || (this.props.columns && this.props.columns.length > 0));
        let simpleDropDown = !(multiSelect || multiColumn);
        let isEmpty = !(this.state.filteredItems && this.state.filteredItems.length > 0)
        let isDisabled = this.props.disabled || this.state.isLoading;// || (!this.props.smartSearch && !this.props.multiSelect && this.props.autoDisable && isEmpty);
        let value = this.state.value == null || this.state.value == undefined || this.state.value == ''
            ? null
            : (this.props.multiSelect
                ? this.state.selectedItems
                : this.getItem(this.state.filteredItems, this.state.value));

        return (
            <this.WrapperDiv>
                {
                    /* A MULTI SELECT SINGLE COLUMN DROP DOWN COMPONENT */
                    multiSelect &&
                    <MultiSelect
                        disabled={isDisabled}
                        data={this.state.filteredItems}
                        onChange={this.onMultiSelectChange}
                        loading={this.state.isLoading}
                        onFilterChange={this.onFilterChange}
                        filterable={this.props.smartSearch || this.props.filterable}
                        label=""
                        //defaultValue={this.props.defaultValue}
                        dataItemKey={this.idField}
                        textField={this.textField}
                        onBlur={this.handleBlur} onFocus={this.handleFocus}
                        dropDownType="MultiSelect"
                        value={this.state.selectedItems} />
                }
                {
                    /* A SINGLE SELECT MULTI COLUMN DROP DOWN COMPONENT */
                    multiColumn &&
                    <MultiColumnComboBox {...this.props as MultiColumnComboBoxProps}
                        data={this.state.filteredItems}
                        disabled={isDisabled}
                        onChange={this.handleDropDownChange}
                        loading={this.state.isLoading}
                        onFilterChange={this.onFilterChange}
                        filterable={this.props.smartSearch || this.props.filterable}
                        label=""
                        //defaultValue={this.props.defaultValue}
                        dataItemKey={this.idField}
                        textField={this.textField}
                        onBlur={this.handleBlur} onFocus={this.handleFocus}
                        value={value}
                        dropDownType="MultiColumn"
                    />
                }
                {
                    /* A SINGLE SELECT SIMPLE DROP DOWN COMPONENT */
                    simpleDropDown &&
                    <ComboBox {...this.props as ComboBoxProps}
                        data={this.state.filteredItems}
                        filterable={this.props.smartSearch || this.props.filterable}
                        disabled={isDisabled}
                        onChange={this.handleDropDownChange}
                        loading={this.state.isLoading}
                        onFilterChange={this.onFilterChange}
                        label=""
                        dataItemKey={this.idField}
                        textField={this.textField}
                        //defaultValue={this.props.defaultValue}
                        onBlur={this.handleBlur} onFocus={this.handleFocus}
                        //value={this.state.valueObject} />
                        value={value} />
                }
            </this.WrapperDiv>
        );
    }
}

export const SmartSearch = (props: any) => <SystemDropDown {...props} smartSearch={true} />
